import { Award, Clock, Code, Star, TrendingUp, Users, Zap } from 'react-feather'
import { LightBulbIcon, SparklesIcon } from '@heroicons/react/24/outline'
import { Brain } from '@components/common/icons/custom-icons'
import { LandingCard } from '@components/coding-landing/types'
import { TocItem } from '@components/coding-landing/table-of-contents'
import { TldrPoint } from '@components/coding-landing/tldr'

export const Ref = ({ num, sup }: { num: number; sup?: boolean }) => (
  <a
    href={`#ref-${num}`}
    className="hover:text-orange"
    aria-label={`Reference ${num}`}
  >
    {sup ? <sup>[{num}]</sup> : `[${num}]`}
  </a>
)

export const BENEFITS_TLDR_POINTS: TldrPoint[] = [
  {
    prefix: 'Poor academic performance is rarely a motivation problem. ',
    highlight:
      "It's a sign the brain hasn't been trained to think systematically",
    suffix: ' - and coding is the most effective way to do that.',
  },
  {
    prefix: 'Children who code ',
    highlight:
      "break problems into steps, think more precisely, and apply what they've learned across subjects",
    suffix: ' - not just in computer science.',
  },
  {
    prefix:
      "Executive function, working memory, and inhibitory control aren't soft skills. ",
    highlight:
      "They're the cognitive systems most strongly linked to academic success",
    suffix: ' - and coding measurably strengthens all three.',
  },
  {
    prefix:
      'The kids who keep performing well as school gets harder share one trait: ',
    highlight: 'they treat every problem as something that can be figured out',
    suffix: ' - not something that happens to them.',
  },
  {
    prefix: "More screen time won't help. More homework won't either. ",
    highlight: 'Changing how your child thinks about problems will',
    suffix: " - and that's exactly what coding does.",
  },
]

export const BENEFITS_TOC: TocItem[] = [
  {
    number: '01',
    title: "How coding rewires your child's brain for better thinking",
  },
  {
    number: '02',
    title: 'Problem-solving skills that transfer to every subject',
  },
  {
    number: '03',
    title: 'Computational thinking: the skill behind every other skill',
  },
  {
    number: '04',
    title: 'Why kids who code perform better in math and writing',
  },
  {
    number: '05',
    title: 'How coding builds confidence and resilience that lasts',
  },
  {
    number: '06',
    title: 'Why starting coding early produces bigger, lasting gains',
  },
  {
    number: '07',
    title: 'The career advantage coding gives kids in any field',
  },
  {
    number: '08',
    title: 'Why kids who enjoy coding learn faster and go further',
  },
  {
    number: '09',
    title: 'Why AI makes learning to code more important, not less',
  },
  { number: '10', title: 'How coding opens the door to robotics and STEM' },
]

export const BENEFITS: LandingCard[] = [
  {
    number: '01',
    icon: Brain,
    sectionBg: 'bg-blue-50',
    heading: "Coding supercharges your child's brain development.",
    bodyParagraphs: [
      <>
        Of all the benefits research has documented, this is the one that
        surprises parents most, and impresses developmental scientists equally.
        Coding does not just teach children to write instructions for a
        computer. It measurably strengthens the executive functions of the
        brain: the systems responsible for planning, focus, working memory, and
        self-control. Research consistently shows that a child&apos;s ability to
        plan, focus, and regulate their own behavior predicts academic success
        more reliably than raw IQ. The primary school years are the best window
        to develop them and it&apos;s the strongest reason{' '}
        <a
          href="/why-should-kids-learn-to-code"
          className="text-blue-200 underline"
          target="_blank"
          rel="noopener noreferrer"
        >
          why kids should learn to code
        </a>{' '}
        before secondary school, not after.
      </>,
    ],
    research:
      'Ribner et al. (2020), NCBI; Montuori et al. (2025), Computers and Education',
    imageSrc: '/images/benefits-of-coding/benefits/brain-development.png',
    imageAlt: 'Brain development through coding',
    whatThisMeans:
      'When your child is debugging code, they are not just fixing a programme. They are training the neural pathways responsible for planning, self-regulation, and focused attention: the same systems that determine how they handle exams, manage their time, and approach every challenge for the rest of their life.',
    statsCard: {
      category: 'Exponential learning curve',
      value: '1 month = 7 months',
      description:
        'One month of coding produces planning improvements equivalent to or greater than 7 months of standard academic activities',
    },
    showCTA: true,
  },
  {
    number: '02',
    icon: LightBulbIcon,
    sectionBg: 'bg-orange-200',
    heading:
      'Coding builds problem-solving skills children use in every subject.',
    bodyParagraphs: [
      <>
        One of the largest independent reviews of coding research ever published
        found that learning to code produces bigger improvements in
        problem-solving than almost any other activity studied, particularly in
        younger children. Researchers have identified specific thinking skills
        that coding strengthens, all of which show up directly in how children
        perform at school and it&apos;s{' '}
        <a
          href="/why-should-kids-learn-to-code"
          className="text-blue-200 underline"
          target="_blank"
          rel="noopener noreferrer"
        >
          why learning to code matters
        </a>
        .<Ref sup num={14} />
      </>,
    ],
    research:
      'Ciftci & Bildiren (2020); Montuori et al. (2024), Computers and Education',
    imageSrc: '/images/benefits-of-coding/benefits/problem-solving-2.png',
    imageAlt: 'Children developing problem-solving skills through coding',
    whatThisMeans:
      "Parents consistently report the same observation: within months, their child's approach to problems outside coding (mathematics, writing, even interpersonal conflicts) changes. Problems that once provoked frustration are met with: 'Let me break this down.'",
    features: [
      {
        label: (
          <span className="uppercase text-orange font-600 text-lg leading-none">
            Causal reasoning
          </span>
        ),
        description: (
          <span className="text-grey-800 text-sm leading-relaxed">
            Understanding cause, effect, and sequential logic
          </span>
        ),
        bgClass: 'bg-green-50 bg-opacity-50',
      },
      {
        label: (
          <span className="uppercase text-orange font-600 text-lg leading-none">
            Spatial reasoning
          </span>
        ),
        description: (
          <span className="text-grey-800 text-sm leading-relaxed">
            Visualizing layouts, movement & relationships between elements
          </span>
        ),
        bgClass: 'bg-blue-50 bg-opacity-50',
      },
      {
        label: (
          <span className="uppercase text-orange font-600 text-lg leading-none">
            Algorithmic thinking
          </span>
        ),
        description: (
          <span className="text-grey-800 text-sm leading-relaxed">
            Understanding how instructions affect outcomes for others
          </span>
        ),
        bgClass: 'bg-yellow-100 bg-opacity-50',
      },
    ],
    quoteCard: {
      image: '/images/benefits-of-coding/benefits/amith-singh.png',
      imageAlt: 'Amith Singh',
      bg: 'bg-orange-200 text-orange-400 border border-orange-300',
      quote: (
        <>
          For a deeper look at how systems thinking develops through text-based
          coding, read{' '}
          <a
            href="https://www.codingal.com/coding-for-kids/blog/text-based-coding-for-kids/"
            className="text-blue-200 underline"
            target="_blank"
            rel="noopener noreferrer"
          >
            Why Text-Based Coding Builds Minds That Think in Systems.
          </a>
        </>
      ),
      attribution: '— Amith Singh',
      attributionRole: "from Codingal's curriculum team",
    },
    showCTA: false,
  },
  {
    number: '03',
    icon: Code,
    sectionBg: 'bg-green-50',
    heading:
      'Coding develops computational thinking: The literacy of the modern era.',
    bodyParagraphs: [
      <>
        Professor Marina Umaschi Bers of Boston College describes this as a
        shift from passive to builder orientation.
        <Ref sup num={6} /> Children who develop computational thinking stop
        seeing problems as things that happen to them. They begin approaching
        every challenge, academic, personal, and professional, with the same
        systematic curiosity and confidence.
      </>,
      <>
        Research with kindergarten-age children showed that children as young as
        4 can engage meaningfully with core principles of computational
        thinking.
        <Ref sup num={5} /> It&apos;s{' '}
        <a
          href="/why-should-kids-learn-to-code"
          className="text-blue-200 underline"
          target="_blank"
          rel="noopener noreferrer"
        >
          why kids should learn to code
        </a>{' '}
        before habits of mind are set.
      </>,
    ],
    research:
      'Ribner et al. (2020), NCBI; Montuori et al. (2024), Computers and Education',
    imageSrc: '/images/benefits-of-coding/benefits/computational-thinking.png',
    imageAlt: 'Child developing computational thinking',
    whatThisMeans:
      'A child who thinks computationally does not wait to be shown the answer. They break the problem apart, test one thing at a time, and adjust when something does not work. Parents notice this shift first in how their child handles frustration.',
    quoteCard: {
      image: '/images/benefits-of-coding/benefits/zaviyar.png',
      imageAlt: 'Zaviyar Khattak',
      bg:
        'bg-yellow-200 bg-opacity-25 text-yellow-200 border border-yellow-200',
      heading: 'From the Codingal Classroom',
      quote:
        '"Two years at Codingal taught me my ideas don\'t have to stay in my head."',
      attribution: '— Zaviyar Khattak',
      attributionRole: 'Codingal student',
    },
    showCTA: true,
  },
  {
    number: '04',
    icon: TrendingUp,
    sectionBg: 'bg-blue-50',
    heading:
      'Children who code perform measurably better in writing and mathematics.',
    bodyParagraphs: [
      <>
        A peer-reviewed study of 49 fifth-grade students in Google&apos;s CS
        First Storytelling lessons found unambiguous results: significant
        increases in writing scores, idea development, essay organization,
        grammar, and stamina.
        <Ref sup num={8} />
      </>,
      <>
        The math connection is equally well-established. Coding applies
        variables, functions, conditional logic, and sequences in a context
        where the purpose is immediately visible.
        <Ref sup num={3} /> When children meet these concepts through code, they
        become tools with a purpose, not rules to memorize.
      </>,
      <>
        The result across multiple studies is improved mathematical intuition.
        <Ref sup num={14} /> For parents still asking whether learning coding is
        worth it, gains across the two most assessed school subjects is a hard
        case to argue with.
      </>,
    ],
    research: 'Google CS First Study, ScienceDirect (2021)',
    imageSrc: '/images/benefits-of-coding/benefits/math-concepts-2.png',
    imageAlt: 'Children improving in writing and mathematics through coding',
    whatThisMeans:
      'When children meet mathematical abstractions through code, they encounter them as tools with a purpose. The result, consistently documented across multiple studies, is improved mathematical intuition and problem-solving ability.',
    quoteCard: {
      image: '/images/benefits-of-coding/benefits/amith-singh.png',
      imageAlt: 'Amith Singh',
      bg: 'bg-blue-100 bg-opacity-25 text-blue-200 border border-blue-100',
      quote: (
        <>
          How Python&apos;s structure turns abstract maths into something
          children navigate with purpose -{' '}
          <a
            href="https://www.codingal.com/coding-for-kids/blog/text-based-coding-for-kids/"
            className="text-blue-200 underline"
            target="_blank"
            rel="noopener noreferrer"
          >
            Why Text-Based Coding Builds Minds That Think in Systems
          </a>
          .
        </>
      ),
      attribution: '— Amith Singh',
      attributionRole: "from Codingal's curriculum team",
    },
    showCTA: false,
  },
  {
    number: '05',
    icon: SparklesIcon,
    sectionBg: 'bg-orange-200',
    heading:
      'Coding builds confidence and resilience through the act of making things.',
    bodyParagraphs: [
      'When a child builds something that works, the experience is qualitatively different from receiving a grade on an essay or a score on a test. Building produces a different kind of knowing.',
      <>
        Completing a working project, whether an app, a game, or an animation,
        delivers a sense of mastery that passive learning cannot replicate. This
        is what builds confidence. Debugging is a structured rehearsal for
        failure. Children learn to treat errors as information, test multiple
        approaches, and persist. These are habits of mind, not techniques:
        resilience. Research by Bers finds that children who build with
        technology stop seeing themselves as consumers and start seeing
        themselves as creators.
        <Ref sup num={6} /> It&apos;s the most underrated answer to{' '}
        <a
          href="/why-should-kids-learn-to-code"
          className="text-blue-200 underline"
          target="_blank"
          rel="noopener noreferrer"
        >
          why learn to code
        </a>
        .
      </>,
    ],
    research:
      'Bers, M.U. (2020), Coding as a Playground; Flannery & Bers (2013)',
    imageSrc: '/images/benefits-of-coding/benefits/develop-resilience-2.png',
    imageAlt: 'Child gaining confidence through coding',
    whatThisMeans:
      "The moment a child's code runs for the first time, something shifts. They built that. Not with help, not from a template. That experience of authorship is what confidence is actually made of, and it compounds every time they build something harder.",
    quoteCard: {
      image: '/images/benefits-of-coding/benefits/freeda-kakara.png',
      imageAlt: 'Freeda Kakara',
      bg: 'bg-orange-200 text-orange-500 border border-orange-300',
      heading:
        'Curious how children as young as 5 begin building this thinking?',
      quote: (
        <>
          Explains how{' '}
          <a
            href="https://www.codingal.com/coding-for-kids/blog/block-based-coding-for-kids/"
            className="text-blue-200 underline"
            target="_blank"
            rel="noopener noreferrer"
          >
            block-based coding provides a science-backed foundation
          </a>{' '}
          showing exactly where and how the journey begins.
        </>
      ),
      attribution: '— Freeda Kakara',
      attributionRole: "from Codingal's curriculum team",
    },
    showCTA: true,
  },
  {
    number: '06',
    icon: Clock,
    sectionBg: 'bg-green-50',
    heading:
      'Starting coding earlier produces measurably larger cognitive gains.',
    bodyParagraphs: [
      <>
        The common parental instinct is to treat coding as something to
        introduce later, around secondary school, but research suggests the
        opposite. A controlled study of 437 children across two groups, ages 5
        to 7 and 8 to 10, found both groups improved in executive function, with
        consistently larger gains in the younger group. Still weighing the
        timing?{' '}
        <a
          href="/is-learning-coding-worth-it"
          className="text-blue-200 underline"
          target="_blank"
          rel="noopener noreferrer"
        >
          Is learning coding worth it{' '}
        </a>{' '}
        answers that question directly.
      </>,
    ],
    research:
      'Ciftci & Bildiren (2020); TangibleK (2013); ScienceDirect (2025)',
    imageSrc: '/images/benefits-of-coding/benefits/structural-thinking-2.png',
    imageAlt: 'Early coding education producing larger cognitive gains',
    whatThisMeans:
      "Most parents assume the window opens at secondary school. The research says it's already closing. What clicks naturally at age 7 takes significantly more effort to develop at 12, and the cognitive foundations built in primary school compound across every subject for years afterward.",
    features: [
      {
        label: (
          <span className="font-700 text-orange-500">
            Critical developmental window (ages 5-10)
          </span>
        ),
        description:
          'Brains are highly receptive to logic, patterns, and structured thinking during primary school years.',
        bgClass: 'bg-green-50 bg-opacity-50',
      },
      {
        label: (
          <span className="font-700 text-orange-500">Earlier is easier</span>
        ),
        description:
          'Skills that feel natural at age 7 often require significantly more effort to learn by age 12.',
        bgClass: 'bg-blue-50 bg-opacity-50',
      },
      {
        label: (
          <span className="font-700 text-orange-500">
            Strong foundation for all learning
          </span>
        ),
        description:
          'Starting coding early builds cognitive abilities that support academic success across subjects for years to come.',
        bgClass: 'bg-yellow-100 bg-opacity-50',
      },
    ],
    quoteCard: {
      bg: 'bg-yellow-100 bg-opacity-50',
      image: '/images/benefits-of-coding/benefits/satyam-baranwal.png',
      imageAlt: 'Satyam Baranwal, Codingal Co-founder',
      quote: (
        <>
          If you&apos;re thinking about why this matters beyond future job
          titles, Codingal&apos;s Co-founder Satyam Baranwal lays it out best:{' '}
          <a
            href="https://www.codingal.com/coding-for-kids/blog/coding-for-kids-new-english/"
            className="text-blue-200 underline"
            target="_blank"
            rel="noopener noreferrer"
          >
            Coding Is the New English
          </a>{' '}
          — Why Every Child Deserves a Seat at the Table. The case isn&apos;t
          about careers. It&apos;s about literacy.
        </>
      ),
      isFullWidth: true,
    },
    showCTA: false,
  },
  {
    number: '07',
    icon: Award,
    sectionBg: 'bg-blue-50',
    heading:
      'Children who code enter the workforce with a measurable and lasting advantage.',
    bodyParagraphs: [
      'The economic case for coding education is unambiguous. Computing and information technology careers are among the fastest-growing, highest-paying, and most resilient occupational categories globally. The career advantage extends well beyond software engineering.',
      <>
        A marketing professional who works with data saves their team weeks
        every quarter. A manager who understands how AI tools work makes better
        decisions. Research from the US Bureau of Labor Statistics and the WEF
        projects that 70% of STEM-related jobs now require coding and AI
        expertise.
        <Ref sup num={9} />
        <Ref sup num={10} />
      </>,
      <>
        For a plain breakdown of what that means for your child, read{' '}
        <a
          href="/is-learning-coding-worth-it"
          className="text-blue-200 underline"
          target="_blank"
          rel="noopener noreferrer"
        >
          is learning coding worth it
        </a>
        .
      </>,
    ],
    research:
      'US Bureau of Labor Statistics (2021); WEF Future of Jobs Report (2025)',
    imageSrc: '/images/benefits-of-coding/benefits/career-advantage.png',
    imageAlt: 'Children building career advantage through coding',
    whatThisMeans:
      "The child in fifth grade who learns to code isn't just learning a skill. They're building the foundation for roles that don't yet have names. Across every field (medicine, finance, education, design), the professionals who understand how technology works will direct the ones who don't.",
    quoteCard: {
      bg: 'bg-white text-grey-500 border border-grey-400',
      quote:
        '"Code has become the 4th literacy. Everyone needs to know how our digital world works, not just engineers."',
      attribution: '— Mark Surman',
      attributionRole: 'Executive Director, Mozilla Foundation',
    },
    showCTA: true,
  },
  {
    number: '08',
    icon: Star,
    sectionBg: 'bg-orange-200',
    heading: 'Coding that children enjoy produces compounding learning gains.',
    bodyParagraphs: [
      <>
        A peer-reviewed study of 86 children aged 9 to 12 examined the link
        between enjoyment and learning outcomes in coding workshops
        <Ref sup num={7} />. The mechanism is specific: fun does not directly
        produce learning. It shapes attitudes toward coding, and those attitudes
        influence how much children believe they have learned and how motivated
        they are to continue. When coding feels engaging rather than
        intimidating, children come back to it voluntarily. That return is what
        drives compounding gains over time. It&apos;s the clearest answer to{' '}
        <a
          href="/why-should-kids-learn-to-code"
          className="text-blue-200 underline"
          target="_blank"
          rel="noopener noreferrer"
        >
          why learn to code
        </a>{' '}
        before motivation becomes a battle.
      </>,
    ],
    research: 'ScienceDirect (2021), structural equation modeling',
    imageSrc: '/images/benefits-of-coding/benefits/enjoyment-gains.png',
    imageAlt: 'Children enjoying coding and producing compounding gains',
    whatThisMeans:
      'A child who finds coding enjoyable does not need to be pushed to practice. They return to it. That voluntary return is what separates children who develop genuine fluency from those who complete a course and stop. Enjoyment is not a nice-to-have in coding education. It is the mechanism.',
    showCTA: false,
  },
  {
    number: '09',
    icon: Zap,
    sectionBg: 'bg-green-50',
    heading: 'AI makes coding more important, not less.',
    bodyParagraphs: [
      'The most frequently asked question parents ask today is a reasonable one: if AI can write code, why should my child bother learning? The premise needs examination. AI generates syntax. It cannot define the problem worth solving, judge whether a solution is correct, or reason about tradeoffs. Those are human capabilities, developed specifically through building things with code.',
      <>
        The WEF Future of Jobs Report projects 170 million new roles in the
        coming decade, with{' '}
        <a
          href="/courses/ai-and-machine-learning"
          className="text-blue-200 underline"
          target="_blank"
          rel="noopener noreferrer"
        >
          AI for kids
        </a>{' '}
        and technology as the fastest-growing sectors.
        <Ref sup num={10} /> Children in first grade today will enter the
        workforce during a period when every industry will require people who
        understand how technology thinks. The question is not whether coding
        matters. It is how soon they begin.
      </>,
    ],
    research: 'WEF Future of Jobs Report (2025); Sam Altman, OpenAI (2025)',
    imageSrc: '/images/benefits-of-coding/benefits/ai-importance.png',
    imageAlt: 'AI makes coding more important for children',
    whatThisMeans:
      'A child who understands how technology thinks will not be replaced by AI. They will be the person directing it. That distinction will determine career trajectories in ways we are only beginning to map.',
    quoteCard: {
      bg:
        'bg-yellow-200 bg-opacity-25 border border-yellow-200 text-yellow-200',
      quote:
        '"When I was graduating, the obvious tactical thing was to get really good at coding. Getting good at AI tools is the new version of that."',
      attribution: '— Sam Altman',
      attributionRole: 'CEO, OpenAI',
    },
    showCTA: true,
  },
  {
    number: '10',
    icon: Users,
    sectionBg: 'bg-orange-200',
    heading: 'Opens the Door to Robotics & Engineering',
    bodyParagraphs: [
      'Coding is the native language of robotics and the entry point to the physical world of STEM. Coding and robotics are not separate disciplines. They share the same foundational thinking. Children who code consistently outperform peers in STEM subjects and demonstrate stronger spatial reasoning, which is one of the strongest predictors of engineering aptitude.',
    ],
    research:
      'Bers, Flannery, Kazakoff & Sullivan (2014), TangibleK Robotics Curriculum; WEF Future of Jobs (2025)',
    imageSrc: '/images/benefits-of-coding/benefits/robotics-engineering.png',
    imageAlt: 'Coding opens the door to robotics and engineering',
    whatThisMeans: (
      <>
        A child who has learned to give precise instructions to a computer has
        already built the core thinking skill that makes robotics intuitive
        rather than overwhelming. Starting with{' '}
        <a
          href="/courses/scratch-programming"
          className="text-blue-200 underline"
          target="_blank"
          rel="noopener noreferrer"
        >
          Scratch for kids
        </a>{' '}
        is one of the most effective ways to build that foundation. Professor
        Marina Bers&apos;s TangibleK research at Boston College found that
        children as young as 6 could program physical objects, developing
        spatial reasoning and systems thinking that extended far beyond the
        screen.
      </>
    ),
    features: [
      {
        label: <span className="font-600 text-grey-700 uppercase">Age 6+</span>,
        sublabel: (
          <span className="font-600 text-lg text-orange-500">
            Can program physical objects
          </span>
        ),
        description: 'Spatial reasoning beyond screen',
        bgClass: 'bg-green-50 bg-opacity-50',
      },
      {
        label: (
          <span className="font-600 text-grey-700 uppercase">
            STEM connection
          </span>
        ),
        sublabel: (
          <span className="font-600 text-lg text-orange-500">
            Direct pathway
          </span>
        ),
        description: 'Robotics, biomedical, autonomous systems',
        bgClass: 'bg-blue-50 bg-opacity-50',
      },
      {
        label: (
          <span className="font-600 text-grey-700 uppercase">Through 2030</span>
        ),
        sublabel: (
          <span className="font-600 text-lg text-orange-500">
            Fastest-growing roles
          </span>
        ),
        description: 'Robotics & automation (WEF)',
        bgClass: 'bg-yellow-100 bg-opacity-50',
      },
    ],
    showCTA: false,
  },
]
